import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  LinearProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Traffic as TrafficIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Route as RouteIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  Thermostat as TemperatureIcon,
  ExpandMore as ExpandMoreIcon,
  DirectionsWalk as WalkIcon,
  DirectionsCar as CarIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

// For Google Maps integration
declare global {
  interface Window {
    initMap: () => void;
    google: any;
  }
}

// Interface for transit details
interface TransitDetail {
  id: string;
  timestamp: string;
  location: {
    coordinates: {
      lat: number;
      lng: number;
    };
    address?: string;
  };
  speed: number;
  heading: number;
  status: 'moving' | 'stopped' | 'loading' | 'unloading' | 'resting';
  notes?: string;
  trafficCondition?: 'light' | 'moderate' | 'heavy';
  weatherCondition?: 'clear' | 'rain' | 'snow' | 'fog' | 'storm';
  temperature?: number;
  fuelLevel?: number;
  distanceTraveled?: number;
  estimatedTimeRemaining?: number; // in minutes
  delayReason?: string;
}

// Interface for shipment data
interface Shipment {
  id: string;
  status: 'pending' | 'in-transit' | 'delivered' | 'delayed';
  trackingNumber: string;
  origin: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  destination: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  currentLocation?: {
    address?: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    updatedAt: string;
    speed: number;
  };
  estimatedDelivery: {
    originalDate?: string;
    date: string;
    timeWindow: string;
    eta?: string; // Estimated time of arrival
  };
  actualDelivery?: {
    date: string;
    time: string;
    signature?: string;
  };
  driver: {
    id: string;
    name: string;
    phone: string;
    avatar?: string;
    rating: number;
  };
  vehicle: {
    type: string;
    registrationNumber: string;
  };
  route: {
    alternateRoutes?: Array<{
      distance: number;
      duration: number;
      trafficCondition: 'light' | 'moderate' | 'heavy';
    }>;
    distance: number;
    duration: number;
    trafficCondition: 'light' | 'moderate' | 'heavy';
    waypoints: Array<{
      name: string;
      coordinates: {
        lat: number;
        lng: number;
      };
      arrivalTime?: string;
      departureTime?: string;
      status: 'pending' | 'arrived' | 'departed';
    }>;
  };
  trafficUpdates?: Array<{
    timestamp: string;
    condition: 'light' | 'moderate' | 'heavy';
    location: string;
    impact: 'none' | 'minor' | 'significant' | 'severe';
    delayMinutes?: number;
  }>;
  updates: Array<{
    timestamp: string;
    message: string;
    type: 'info' | 'warning' | 'success';
  }>;
}

// Interface for the component props
interface DriverLocationTrackingProps {
  shipment: Shipment;
  onLocationUpdate?: (location: { lat: number; lng: number; speed: number; heading: number }) => void;
  onStatusUpdate?: (status: string) => void;
  onTrafficUpdate?: (trafficData: any) => void;
  onETAUpdate?: (eta: string) => void;
  onTransitDetailRecorded?: (detail: TransitDetail) => void;
}

/**
 * DriverLocationTracking component for real-time tracking of driver location
 * Records transit details and provides traffic updates and ETA information
 * @param shipment - The shipment being delivered
 * @param onLocationUpdate - Callback function when driver location is updated
 * @param onStatusUpdate - Callback function when driver status changes
 * @param onTrafficUpdate - Callback function when traffic conditions change
 * @param onETAUpdate - Callback function when ETA is updated
 * @param onTransitDetailRecorded - Callback function when a transit detail is recorded
 * @returns React component for driver location tracking
 */
const DriverLocationTracking: React.FC<DriverLocationTrackingProps> = ({
  shipment,
  onLocationUpdate,
  onStatusUpdate,
  onTrafficUpdate,
  onETAUpdate,
  onTransitDetailRecorded,
}) => {
  // Reference to the map container element
  const mapRef = useRef<HTMLDivElement>(null);
  
  // State for map and related services
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const [trafficLayer, setTrafficLayer] = useState<google.maps.TrafficLayer | null>(null);
  const [driverMarker, setDriverMarker] = useState<google.maps.Marker | null>(null);
  
  // State for map loading status
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // State for location sharing
  const [sharingLocation, setSharingLocation] = useState(false);
  
  // State for location permission status
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  
  // State for current position
  const [currentPosition, setCurrentPosition] = useState<GeolocationPosition | null>(null);
  
  // State for location update interval ID
  const [locationUpdateInterval, setLocationUpdateInterval] = useState<number | null>(null);
  
  // State for showing traffic layer
  const [showTraffic, setShowTraffic] = useState(true);
  
  // State for driver status
  const [driverStatus, setDriverStatus] = useState<'moving' | 'stopped' | 'loading' | 'unloading' | 'resting'>('moving');
  
  // State for transit details
  const [transitDetails, setTransitDetails] = useState<TransitDetail[]>([]);
  
  // State for transit detail dialog
  const [transitDetailDialogOpen, setTransitDetailDialogOpen] = useState(false);
  const [currentTransitDetail, setCurrentTransitDetail] = useState<TransitDetail | null>(null);
  
  // State for notes
  const [notes, setNotes] = useState('');
  
  // State for traffic condition
  const [trafficCondition, setTrafficCondition] = useState<'light' | 'moderate' | 'heavy'>('light');
  
  // State for weather condition
  const [weatherCondition, setWeatherCondition] = useState<'clear' | 'rain' | 'snow' | 'fog' | 'storm'>('clear');
  
  // State for temperature
  const [temperature, setTemperature] = useState<number | null>(null);
  
  // State for fuel level
  const [fuelLevel, setFuelLevel] = useState<number>(100);
  
  // State for distance traveled
  const [distanceTraveled, setDistanceTraveled] = useState<number>(0);
  
  // State for estimated time remaining
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  
  // State for delay reason
  const [delayReason, setDelayReason] = useState<string>('');
  
  // State for loading status
  const [loading, setLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  
  /**
   * Initialize the map when the component mounts
   */
  useEffect(() => {
    if (window.google && mapRef.current && !map) {
      initializeMap();
    } else {
      // Load Google Maps script if not already loaded
      if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places,directions`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (mapRef.current) {
            initializeMap();
          }
        };
        document.head.appendChild(script);
      }
    }
    
    // Check for geolocation permission
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
      });
    }
    
    // Cleanup function
    return () => {
      if (locationUpdateInterval) {
        clearInterval(locationUpdateInterval);
      }
    };
  }, []);
  
  /**
   * Initialize the Google Map
   */
  const initializeMap = () => {
    if (!mapRef.current) return;
    
    const mapOptions = {
      zoom: 12,
      center: { lat: shipment.origin.coordinates.lat, lng: shipment.origin.coordinates.lng },
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    };
    
    try {
      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);
      
      try {
        const newDirectionsService = new window.google.maps.DirectionsService();
        setDirectionsService(newDirectionsService);
        
        const newDirectionsRenderer = new window.google.maps.DirectionsRenderer({
          map: newMap,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: '#4285F4',
            strokeWeight: 5,
            strokeOpacity: 0.8,
          },
        });
        setDirectionsRenderer(newDirectionsRenderer);
        
        const newTrafficLayer = new window.google.maps.TrafficLayer();
        if (showTraffic) {
          newTrafficLayer.setMap(newMap);
        }
        setTrafficLayer(newTrafficLayer);
        
        // Create origin marker
        new window.google.maps.Marker({
          position: shipment.origin.coordinates,
          map: newMap,
          title: 'Origin: ' + shipment.origin.name,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
          },
        });
        
        // Create destination marker
        new window.google.maps.Marker({
          position: shipment.destination.coordinates,
          map: newMap,
          title: 'Destination: ' + shipment.destination.name,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          },
        });
        
        // Create waypoint markers
        shipment.route.waypoints.forEach((waypoint) => {
          new window.google.maps.Marker({
            position: waypoint.coordinates,
            map: newMap,
            title: waypoint.name,
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
            },
          });
        });
        
        // Create driver marker if current location exists
        if (shipment.currentLocation) {
          const newDriverMarker = new window.google.maps.Marker({
            position: shipment.currentLocation.coordinates,
            map: newMap,
            title: 'Driver: ' + shipment.driver.name,
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            },
          });
          setDriverMarker(newDriverMarker);
        }
        
        // Calculate and display route
        calculateRoute();
      } catch (error) {
        console.error('Error initializing map components:', error);
        setError('Failed to initialize map components. Please try refreshing the page.');
      }
      
      setMapLoaded(true);
    } catch (error) {
      console.error('Error creating map:', error);
      setError('Failed to create map. Please try refreshing the page.');
    }
  };
  
  /**
   * Calculate and display the route on the map
   */
  const calculateRoute = () => {
    if (!directionsService || !directionsRenderer) return;
    
    const waypoints = shipment.route.waypoints.map((waypoint) => ({
      location: new window.google.maps.LatLng(waypoint.coordinates.lat, waypoint.coordinates.lng),
      stopover: true,
    }));
    
    const request = {
      origin: new window.google.maps.LatLng(shipment.origin.coordinates.lat, shipment.origin.coordinates.lng),
      destination: new window.google.maps.LatLng(shipment.destination.coordinates.lat, shipment.destination.coordinates.lng),
      waypoints: waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
      drivingOptions: {
        departureTime: new Date(),
        trafficModel: window.google.maps.TrafficModel.BEST_GUESS,
      },
    };
    
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        directionsRenderer.setDirections(result);
        
        // Update estimated time based on traffic
        if (result && result.routes && result.routes.length > 0) {
          const route = result.routes[0];
          let totalDuration = 0;
          let totalDistance = 0;
          
          if (route.legs) {
            route.legs.forEach((leg) => {
              if (leg.duration && leg.duration.value) {
                totalDuration += leg.duration.value;
              }
              if (leg.distance && leg.distance.value) {
                totalDistance += leg.distance.value;
              }
            });
          }
          
          // Convert seconds to minutes
          const durationInMinutes = Math.round(totalDuration / 60);
          // Convert meters to kilometers
          const distanceInKm = Math.round(totalDistance / 1000 * 10) / 10;
          
          setEstimatedTimeRemaining(durationInMinutes);
          
          // Update ETA
          const now = new Date();
          const eta = new Date(now.getTime() + totalDuration * 1000);
          const etaString = eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          
          if (onETAUpdate) {
            onETAUpdate(etaString);
          }
          
          // Check traffic conditions
          let trafficCondition: 'light' | 'moderate' | 'heavy' = 'light';
          if (route.legs && route.legs.length > 0) {
            const leg = route.legs[0];
            if (leg.duration_in_traffic && leg.duration) {
              const ratio = leg.duration_in_traffic.value / leg.duration.value;
              if (ratio > 1.5) {
                trafficCondition = 'heavy';
              } else if (ratio > 1.2) {
                trafficCondition = 'moderate';
              }
            }
          }
          
          setTrafficCondition(trafficCondition);
          
          if (onTrafficUpdate) {
            onTrafficUpdate({
              condition: trafficCondition,
              location: 'Current route',
              impact: trafficCondition === 'heavy' ? 'significant' : trafficCondition === 'moderate' ? 'minor' : 'none',
              delayMinutes: trafficCondition === 'heavy' ? 15 : trafficCondition === 'moderate' ? 5 : 0,
            });
          }
        }
      } else {
        console.error('Directions request failed due to ' + status);
        setError('Failed to calculate route. Please try again.');
      }
    });
  };
  
  /**
   * Toggle traffic layer visibility
   */
  const handleToggleTraffic = () => {
    if (trafficLayer && map) {
      if (showTraffic) {
        trafficLayer.setMap(null);
      } else {
        trafficLayer.setMap(map);
      }
      setShowTraffic(!showTraffic);
    }
  };
  
  /**
   * Start sharing location
   */
  const startSharingLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    
    setSharingLocation(true);
    
    // Get current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentPosition(position);
        updateDriverMarker(position);
        
        // Set up interval to update position
        const intervalId = window.setInterval(() => {
          navigator.geolocation.getCurrentPosition(
            (newPosition) => {
              setCurrentPosition(newPosition);
              updateDriverMarker(newPosition);
            },
            (err) => {
              console.error('Error getting position:', err);
              setError('Failed to get your current location');
            },
            { enableHighAccuracy: true }
          );
        }, 10000); // Update every 10 seconds
        
        setLocationUpdateInterval(intervalId);
      },
      (err) => {
        console.error('Error getting position:', err);
        setError('Failed to get your current location');
        setSharingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };
  
  /**
   * Stop sharing location
   */
  const stopSharingLocation = () => {
    if (locationUpdateInterval) {
      clearInterval(locationUpdateInterval);
      setLocationUpdateInterval(null);
    }
    setSharingLocation(false);
  };
  
  /**
   * Update driver marker position on the map
   * @param position - The new geolocation position
   */
  const updateDriverMarker = (position: GeolocationPosition) => {
    const { latitude, longitude } = position.coords;
    const speed = position.coords.speed || 0;
    const heading = position.coords.heading || 0;
    
    if (map) {
      const latLng = new window.google.maps.LatLng(latitude, longitude);
      
      if (driverMarker) {
        driverMarker.setPosition(latLng);
      } else {
        const newDriverMarker = new window.google.maps.Marker({
          position: latLng,
          map: map,
          title: 'Driver: ' + shipment.driver.name,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          },
        });
        setDriverMarker(newDriverMarker);
      }
      
      // Center map on driver position
      map.panTo(latLng);
    }
    
    // Call location update callback
    if (onLocationUpdate) {
      onLocationUpdate({
        lat: latitude,
        lng: longitude,
        speed: speed,
        heading: heading,
      });
    }
    
    // Record transit detail
    recordTransitDetail({
      lat: latitude,
      lng: longitude,
      speed: speed,
      heading: heading,
    });
  };
  
  /**
   * Record a transit detail
   * @param location - The current location data
   */
  const recordTransitDetail = (location: { lat: number; lng: number; speed: number; heading: number }) => {
    const now = new Date();
    const timestamp = now.toISOString();
    
    const transitDetail: TransitDetail = {
      id: `td-${Date.now()}`,
      timestamp: timestamp,
      location: {
        coordinates: {
          lat: location.lat,
          lng: location.lng,
        },
      },
      speed: location.speed,
      heading: location.heading,
      status: driverStatus,
      trafficCondition: trafficCondition,
      weatherCondition: weatherCondition,
      temperature: temperature || undefined,
      fuelLevel: fuelLevel,
      distanceTraveled: distanceTraveled,
      estimatedTimeRemaining: estimatedTimeRemaining || undefined,
      delayReason: delayReason || undefined,
    };
    
    // Get address from coordinates using Google Maps Geocoding API
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
          if (status === 'OK' && results && results[0]) {
            transitDetail.location.address = results[0].formatted_address;
          }
          
          // Add to transit details
          setTransitDetails((prev) => [...prev, transitDetail]);
          
          // Call transit detail recorded callback
          if (onTransitDetailRecorded) {
            onTransitDetailRecorded(transitDetail);
          }
        }
      );
    } else {
      // Add to transit details without address
      setTransitDetails((prev) => [...prev, transitDetail]);
      
      // Call transit detail recorded callback
      if (onTransitDetailRecorded) {
        onTransitDetailRecorded(transitDetail);
      }
    }
  };
  
  /**
   * Update driver status
   * @param status - The new status
   */
  const updateDriverStatus = (status: 'moving' | 'stopped' | 'loading' | 'unloading' | 'resting') => {
    setDriverStatus(status);
    
    if (onStatusUpdate) {
      onStatusUpdate(status);
    }
    
    // Record transit detail with new status
    if (currentPosition) {
      const { latitude, longitude } = currentPosition.coords;
      const speed = currentPosition.coords.speed || 0;
      const heading = currentPosition.coords.heading || 0;
      
      recordTransitDetail({
        lat: latitude,
        lng: longitude,
        speed: speed,
        heading: heading,
      });
    }
  };
  
  /**
   * Open transit detail dialog
   */
  const openTransitDetailDialog = () => {
    if (currentPosition) {
      const { latitude, longitude } = currentPosition.coords;
      const speed = currentPosition.coords.speed || 0;
      const heading = currentPosition.coords.heading || 0;
      
      const now = new Date();
      const timestamp = now.toISOString();
      
      const transitDetail: TransitDetail = {
        id: `td-${Date.now()}`,
        timestamp: timestamp,
        location: {
          coordinates: {
            lat: latitude,
            lng: longitude,
          },
        },
        speed: speed,
        heading: heading,
        status: driverStatus,
        notes: '',
        trafficCondition: trafficCondition,
        weatherCondition: weatherCondition,
        temperature: temperature || undefined,
        fuelLevel: fuelLevel,
        distanceTraveled: distanceTraveled,
        estimatedTimeRemaining: estimatedTimeRemaining || undefined,
        delayReason: delayReason || undefined,
      };
      
      setCurrentTransitDetail(transitDetail);
      setTransitDetailDialogOpen(true);
    }
  };
  
  /**
   * Close transit detail dialog
   */
  const closeTransitDetailDialog = () => {
    setTransitDetailDialogOpen(false);
    setCurrentTransitDetail(null);
    setNotes('');
  };
  
  /**
   * Save transit detail
   */
  const saveTransitDetail = () => {
    if (currentTransitDetail) {
      const updatedDetail = {
        ...currentTransitDetail,
        notes: notes,
        trafficCondition: trafficCondition,
        weatherCondition: weatherCondition,
        temperature: temperature || undefined,
        fuelLevel: fuelLevel,
        distanceTraveled: distanceTraveled,
        estimatedTimeRemaining: estimatedTimeRemaining || undefined,
        delayReason: delayReason || undefined,
      };
      
      // Add to transit details
      setTransitDetails((prev) => [...prev, updatedDetail]);
      
      // Call transit detail recorded callback
      if (onTransitDetailRecorded) {
        onTransitDetailRecorded(updatedDetail);
      }
      
      setSuccess('Transit detail recorded successfully');
      closeTransitDetailDialog();
    }
  };
  
  /**
   * Refresh route and traffic information
   */
  const refreshRouteAndTraffic = () => {
    setLoading(true);
    calculateRoute();
    setLoading(false);
  };
  
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 2, height: '500px', position: 'relative' }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000 }}>
                <LinearProgress />
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" component="h2">
                <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Driver Location Tracking
              </Typography>
              
              <Box>
                <Tooltip title="Toggle Traffic Layer">
                  <IconButton onClick={handleToggleTraffic} color={showTraffic ? 'primary' : 'default'}>
                    <TrafficIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Refresh Route">
                  <IconButton onClick={refreshRouteAndTraffic} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Box
              ref={mapRef}
              sx={{
                width: '100%',
                height: 'calc(100% - 50px)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: '500px', overflow: 'auto' }}>
            <Typography variant="h6" component="h2" gutterBottom>
              <TruckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Driver Controls
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <ShareIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'small' }} />
                Location Sharing
              </Typography>
              
              {locationPermission === 'denied' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Location permission denied. Please enable location services to share your location.
                </Alert>
              )}
              
              {!sharingLocation ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MyLocationIcon />}
                  onClick={startSharingLocation}
                  disabled={locationPermission === 'denied'}
                  fullWidth
                >
                  Start Sharing Location
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={stopSharingLocation}
                  fullWidth
                >
                  Stop Sharing Location
                </Button>
              )}
              
              {currentPosition && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <LocationIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle' }} />
                    Lat: {currentPosition.coords.latitude.toFixed(6)}, Lng: {currentPosition.coords.longitude.toFixed(6)}
                  </Typography>
                  <Typography variant="body2">
                    <SpeedIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle' }} />
                    Speed: {(currentPosition.coords.speed || 0).toFixed(1)} m/s
                  </Typography>
                  <Typography variant="body2">
                    <NavigationIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle' }} />
                    Heading: {currentPosition.coords.heading || 'N/A'}
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <CarIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'small' }} />
                Driver Status
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="driver-status-label">Current Status</InputLabel>
                <Select
                  labelId="driver-status-label"
                  value={driverStatus}
                  label="Current Status"
                  onChange={(e) => updateDriverStatus(e.target.value as any)}
                  disabled={!sharingLocation}
                >
                  <MenuItem value="moving">
                    <PlayIcon sx={{ mr: 1, fontSize: 'small' }} /> Moving
                  </MenuItem>
                  <MenuItem value="stopped">
                    <PauseIcon sx={{ mr: 1, fontSize: 'small' }} /> Stopped
                  </MenuItem>
                  <MenuItem value="loading">
                    <CloudUploadIcon sx={{ mr: 1, fontSize: 'small' }} /> Loading
                  </MenuItem>
                  <MenuItem value="unloading">
                    <CloudUploadIcon sx={{ mr: 1, fontSize: 'small', transform: 'rotate(180deg)' }} /> Unloading
                  </MenuItem>
                  <MenuItem value="resting">
                    <WalkIcon sx={{ mr: 1, fontSize: 'small' }} /> Resting
                  </MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={openTransitDetailDialog}
                disabled={!sharingLocation}
                fullWidth
              >
                Record Transit Detail
              </Button>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'small' }} />
                Shipment Information
              </Typography>
              
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Tracking Number" 
                    secondary={shipment.trackingNumber} 
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Origin" 
                    secondary={shipment.origin.name} 
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Destination" 
                    secondary={shipment.destination.name} 
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Estimated Delivery" 
                    secondary={`${shipment.estimatedDelivery.date} (${shipment.estimatedDelivery.timeWindow})`} 
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {estimatedTimeRemaining !== null && (
                  <ListItem disableGutters>
                    <ListItemText 
                      primary="Estimated Time Remaining" 
                      secondary={`${Math.floor(estimatedTimeRemaining / 60)}h ${estimatedTimeRemaining % 60}m`} 
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                )}
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Traffic Condition" 
                    secondary={
                      <Typography variant="body2" component="div">
                        <Chip 
                          size="small" 
                          label={trafficCondition.charAt(0).toUpperCase() + trafficCondition.slice(1)} 
                          color={trafficCondition === 'heavy' ? 'error' : trafficCondition === 'moderate' ? 'warning' : 'success'} 
                          variant="outlined" 
                        />
                      </Typography>
                    } 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'small' }} />
                Transit History
              </Typography>
              
              {transitDetails.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No transit details recorded yet.
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {transitDetails.slice().reverse().map((detail) => (
                    <ListItem key={detail.id} disableGutters>
                      <ListItemText
                        primary={
                          <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" component="span">
                              {new Date(detail.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {' - '}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={detail.status.charAt(0).toUpperCase() + detail.status.slice(1)} 
                              color={detail.status === 'moving' ? 'success' : 'default'} 
                              variant="outlined" 
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption">
                            {detail.location.address || `Lat: ${detail.location.coordinates.lat.toFixed(6)}, Lng: ${detail.location.coordinates.lng.toFixed(6)}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Transit Detail Dialog */}
      <Dialog open={transitDetailDialogOpen} onClose={closeTransitDetailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SaveIcon sx={{ mr: 1 }} />
            Record Transit Detail
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="traffic-condition-label">Traffic Condition</InputLabel>
                <Select
                  labelId="traffic-condition-label"
                  value={trafficCondition}
                  label="Traffic Condition"
                  onChange={(e) => setTrafficCondition(e.target.value as any)}
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="moderate">Moderate</MenuItem>
                  <MenuItem value="heavy">Heavy</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="weather-condition-label">Weather Condition</InputLabel>
                <Select
                  labelId="weather-condition-label"
                  value={weatherCondition}
                  label="Weather Condition"
                  onChange={(e) => setWeatherCondition(e.target.value as any)}
                >
                  <MenuItem value="clear">Clear</MenuItem>
                  <MenuItem value="rain">Rain</MenuItem>
                  <MenuItem value="snow">Snow</MenuItem>
                  <MenuItem value="fog">Fog</MenuItem>
                  <MenuItem value="storm">Storm</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Temperature (°C)"
                type="number"
                value={temperature === null ? '' : temperature}
                onChange={(e) => setTemperature(e.target.value === '' ? null : Number(e.target.value))}
                fullWidth
                InputProps={{
                  endAdornment: <TemperatureIcon />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fuel Level (%)"
                type="number"
                value={fuelLevel}
                onChange={(e) => setFuelLevel(Number(e.target.value))}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Distance Traveled (km)"
                type="number"
                value={distanceTraveled}
                onChange={(e) => setDistanceTraveled(Number(e.target.value))}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delay Reason (if any)"
                value={delayReason}
                onChange={(e) => setDelayReason(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTransitDetailDialog}>Cancel</Button>
          <Button onClick={saveTransitDetail} variant="contained" startIcon={<SaveIcon />}>
            Save Detail
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverLocationTracking;